import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { Sort } from "@angular/material/sort";
import { MatDialog } from "@angular/material/dialog";
import { SaveService } from "../../../services/save.service";
import { DefaultValuesService } from "../../../services/defaultvalues.service";
import { CustomDialogService } from "../../../services/customdialog.service";
import { Utils } from "../../../utils/utils";
import { LogsHistoryPopupComponent } from "../logshistorypopup/logshistorypopup.component";

@Component({
    selector: "allowedactions-list",
    templateUrl: './allowedactions-list.component.html',
    styleUrls: ['./allowedactions-list.component.less'],
    standalone: false
})

export class AllowedActionListComponent implements OnInit {
    @Input('list') list: any[] = [];
    @Input('isEditableMethod') isEditable: any
    @Output() onReload = new EventEmitter<any>();


    columnsToDisplay = ['CommandId', 'CommandName', 'Category', 'Description','FirstActivationUtc', 'LastChangeUtc', 'actions'];
    dateColumns = ['FirstActivationUtc', 'LastChangeUtc']
    alwaysActiveSelectorDefaultValue = 'showAll';
    showTable = false;

    @ViewChild(MatTable, { static: true }) table!: MatTable<any>;

    public dataSource = new MatTableDataSource<any>();

    subscriptionTenant: any;
    lastSort: Sort = {active: '', direction: ''};

    timeoutFunction: any;

    constructor(private readonly activatedRoute: ActivatedRoute
        , private readonly router: Router
        , private readonly http: HttpClient
        , private readonly saveService: SaveService
        , public readonly defaults: DefaultValuesService
        , private readonly customDialog: CustomDialogService
        , @Inject('BASE_URL') private readonly baseUrl: string
        , private readonly changeDetectorRefs: ChangeDetectorRef
        , private readonly dialog: MatDialog
    ) {
    }

    openLogsHistory() {
        this.dialog.open(LogsHistoryPopupComponent, {
            width: '80vw',
            maxHeight: '90vh',
            data: { 
                section: 'allowedactions',
                tenantId: this.defaults.tenant,
                controllerTypeId: this.defaults.controllertype.ControllerTypeID
            }
        });
    }

    ngOnChanges() {
        this.dataSource.data = [];
        this.dataSource.data = this.list;
        this.applyDefaultSort();
        this.refreshTable();
    }

    ngOnInit() {
        this.getHeader();
        this.subscriptionTenant = this.defaults.tenantMessageChanges$.subscribe(() => {
            this.getHeader()
        });
        this.saveService.addListener(this);
    }

    ngOnDestroy() {
        this.subscriptionTenant.unsubscribe();
    }

    getHeader() {
            this.columnsToDisplay = ['CommandId', 'CommandName', 'Category', 'Description', 'FirstActivationUtc', 'LastChangeUtc', 'actions'];
    }

    getInput(data: AllowedRemoteAction, property: string): any {
        return data[property];
    }

    getDataType(property: string) {
        let result = ''
        if (this.dateColumns.indexOf(property) > -1) result = 'date'
        else result = 'string'
        return result
    }


    applyFilter($event: any) {
        if (this.timeoutFunction) {
            clearTimeout(this.timeoutFunction);
            this.timeoutFunction = null;
        }
        this.timeoutFunction = setTimeout(() => {
            this.dataSource.filter = $event.target.value.trim()
        }, 300)
    }

    applyAllwaysActiveFilter($event: any) {
        if ($event.value === 'showAll') {
            this.dataSource.data = this.list;
        }
        else if ($event.value === 'alwaysActive') {
            this.dataSource.data = this.list.filter(x => x.AlwaysActive === true);
        }
        else if ($event.value === 'nonAlwaysActive') {
            this.dataSource.data = this.list.filter(x => x.AlwaysActive === false);
        }
        
    }

    changeElement(object: any, type: string, value: boolean) {
        this.http
            .post<any>(
                this.baseUrl + Utils.getRemoteActionAPI() + 'allowedaction' + '/' + object.CommandId + '/' + value, {}
            ).subscribe({
                next: (res) => {
                    // Update the local object directly
                    object.Active = value;
                    // Refresh the table without waiting for the parent
                    this.dataSource.data = [...this.dataSource.data];
                },
                error: (error) => {
                    this.customDialog.openDisplayInfoDialog({ title: 'Error', message: error.error.message });
                }
            });
    }

    getCommandLabel(data) {
        var transl = data['CommandLabel']
        return transl ? transl : data['CommandLabelRef'];
    }
    sortData(sort: Sort) {
        const data = this.dataSource.data.slice();

        // Cycle: default -> asc -> desc -> default
        if (
            this.lastSort.active === sort.active &&
            this.lastSort.direction === sort.direction
        ) {
            // If user clicks again, reset to default
            sort = {active: '', direction: ''};
        }
        this.lastSort = {...sort};

        if (!sort.active || sort.direction === '') {
            const enabled = data
                .filter(x => !!x.Active || !!x.AlwaysActive)
                .sort((a, b) => a.CommandId - b.CommandId);
            const disabled = data
                .filter(x => !x.Active && !x.AlwaysActive)
                .sort((a, b) => a.CommandId - b.CommandId);
            this.dataSource.data = [...enabled, ...disabled];
            return;
        }

        this.dataSource.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'CommandId':
                    return this.compare(a.CommandId, b.CommandId, isAsc);
                case 'CommandName':
                    return this.compare(
                        this.getCommandLabel(a)?.toUpperCase() || '',
                        this.getCommandLabel(b)?.toUpperCase() || '',
                        isAsc
                    );
                case 'Category':
                    return this.compare(
                        (a['Category'] ?? '').toUpperCase(),
                        (b['Category'] ?? '').toUpperCase(),
                        isAsc
                    );
                default:
                    return 0;
            }
        });
    }

    compare(a: number | string, b: number | string, isAsc: boolean) {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    refreshTable() {
        this.showTable = false;
        setTimeout(() => {
            this.dataSource.data = [...this.list];
            this.applyDefaultSort(); // Ensure default sort after refresh
            this.showTable = true;
        }, 0);
    }

    // Add this new method to encapsulate your default sort logic
    applyDefaultSort() {
        const data = this.dataSource.data.slice();
        const enabled = data
            .filter(x => !!x.Active || !!x.AlwaysActive)
            .sort((a, b) => a.CommandId - b.CommandId);
        const disabled = data
            .filter(x => !x.Active && !x.AlwaysActive)
            .sort((a, b) => a.CommandId - b.CommandId);
        this.dataSource.data = [...enabled, ...disabled];
    }
}